"use client";

import { useState, useRef, useEffect } from "react";
import { PhotoLightbox } from "@/app/components/PhotoLightbox";
import Link from "next/link";
import Image from "next/image";
import { useRouter } from "next/navigation";
import {
  ROLES_TREE,
  LOCATIONS_TREE,
  QUALIFICATIONS_TREE,
  EXPERIENCE_LEVELS,
  findLabelById,
  type LanguageEntry,
  type RightToWorkEntry,
  getInformalNationality,
} from "@/lib/taxonomy";
import { toggleShortlist } from "@/app/actions/shortlist";
import { EmployerContactRequirements, useEmployerContactRequirements } from "@/app/_components/marketplace/EmployerContactRequirements";
import { profileQualificationRecords, type QualificationRecord } from "@/lib/marketplace/eligibility";
import { sendFirstMessage } from "@/app/actions/messaging";
import type { Vouch } from "@/app/actions/vouches";
import { VouchModal } from "./VouchModal";
import { trackEvent } from "@/lib/analytics";
import { ProfileShareCard } from "@/app/components/ProfileShareCard";
import type { LinkedProfile } from "@/app/actions/profile-links";

// ═══ TYPES ═══

interface Profile {
  id: string;
  user_id: string;
  first_name: string;
  last_name: string;
  photo_url: string | null;
  video_url: string | null;
  bio: string | null;
  current_location: string | null;
  nationality: string | null;
  gender: string | null;
  dob: string | null;
  birth_date: string | null;
  right_to_work: RightToWorkEntry[];
  experience_level: string | null;
  qualifications: string[];
  qualification_records?: QualificationRecord[];
  languages: LanguageEntry[] | string | null;
  last_active: string;
  is_sample: boolean;
  featured: boolean;
  boost_source: 'referral' | 'ambassador' | 'season_edit' | 'admin' | null;
  referral_code: string | null;
  work_profile_paused: boolean;
}

interface AvailabilityWindow {
  id: string;
  season_label: string;
  available_from: string;
  available_to: string;
  dates_flexible: boolean;
  flexible_earliest_start: string | null;
  flexible_latest_end: string | null;
  positions: string[];
  preferred_locations: string[];
  status: string;
  placed_at_company: string | null;
}

interface PlacementDateRange {
  start: string;
  end: string;
}

interface EmployerInfo {
  id: string;
  company_name: string;
  contact_name: string;
  hiring_for: string[];
  daily_message_limit: number;
}

// ═══ CONSTANTS ═══

const AVATAR_GRADIENTS = [
  "from-navy to-navy-mid",
  "from-terracotta to-gold",
  "from-sage to-[#5A7A5F]",
  "from-[#6A5ACD] to-[#8B78DB]",
  "from-[#2E86AB] to-[#45A0C4]",
  "from-navy to-sage",
];

// ═══ HELPERS ═══

function getInitials(first: string, last: string) {
  return `${first.charAt(0)}${last.charAt(0)}`.toUpperCase();
}

function getDaysSinceActive(dateStr: string | null): number | null {
  if (!dateStr) return null;
  return Math.floor(
    (Date.now() - new Date(dateStr).getTime()) / (24 * 60 * 60 * 1000)
  );
}

function getActivityInfo(dateStr: string | null): { dotClass: string | null; label: string } {
  const days = getDaysSinceActive(dateStr);
  if (days === null) return { dotClass: null, label: "New" };
  if (days < 1) return { dotClass: "bg-success", label: "Active today" };
  if (days === 1) return { dotClass: "bg-[#F59E0B]", label: "Active 1 day ago" };
  if (days < 7) return { dotClass: "bg-[#F59E0B]", label: `Active ${days} days ago` };
  const weeks = Math.floor(days / 7);
  if (weeks < 8) return { dotClass: "bg-text-muted", label: `Active ${weeks}w ago` };
  const months = Math.floor(days / 30);
  return { dotClass: "bg-text-muted", label: `Active ${months}mo ago` };
}

function getAge(dob: string): number | null {
  const birth = new Date(dob);
  if (isNaN(birth.getTime())) return null;
  const today = new Date();
  let age = today.getFullYear() - birth.getFullYear();
  const m = today.getMonth() - birth.getMonth();
  if (m < 0 || (m === 0 && today.getDate() < birth.getDate())) age--;
  return age;
}

function parseLanguages(
  langs: LanguageEntry[] | string | null
): LanguageEntry[] {
  if (!langs) return [];
  if (typeof langs === "string") {
    try {
      return JSON.parse(langs);
    } catch {
      return [];
    }
  }
  return langs;
}

function getYouTubeId(url: string): string | null {
  const match = url.match(
    /(?:youtube\.com\/(?:watch\?v=|embed\/)|youtu\.be\/)([a-zA-Z0-9_-]{11})/
  );
  return match?.[1] ?? null;
}

function getVimeoId(url: string): string | null {
  const match = url.match(/vimeo\.com\/(\d+)/);
  return match?.[1] ?? null;
}

// ═══ MESSAGE MODAL ═══

function MessageModal({
  profile,
  employer,
  availabilityWindowId,
  onClose,
  onSent,
}: {
  profile: Profile;
  employer: EmployerInfo;
  availabilityWindowId: string;
  onClose: () => void;
  onSent: (conversationId: string) => void;
}) {
  const [message, setMessage] = useState("");
  const contact = useEmployerContactRequirements(profile.id);
  const [sending, setSending] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const textareaRef = useRef<HTMLTextAreaElement>(null);

  useEffect(() => {
    textareaRef.current?.focus();
    const handler = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    document.addEventListener("keydown", handler);
    document.body.style.overflow = "hidden";
    return () => {
      document.removeEventListener("keydown", handler);
      document.body.style.overflow = "";
    };
  }, [onClose]);

  async function handleSend() {
    if (!message.trim()) return;
    if (contact.error) { setError(contact.error); return; }
    setSending(true);
    setError(null);
    const result = await sendFirstMessage(profile.id, message.trim(), availabilityWindowId, contact.listingId || undefined);
    if (result.error) {
      if (result.conversationId) { onSent(result.conversationId); return; }
      setError(result.error);
      setSending(false);
    } else if (result.conversationId) {
      trackEvent("message_sent", { candidateId: profile.id, initiated_by: "employer" });
      onSent(result.conversationId);
    }
  }

  return (
    <div
      className="fixed inset-0 z-[200] flex items-center justify-center p-4"
      onClick={(e) => e.target === e.currentTarget && onClose()}
    >
      <div className="absolute inset-0 bg-black/50 backdrop-blur-[4px]" />
      <div className="relative z-10 bg-white rounded-[16px] p-5 sm:p-7 md:p-9 w-full max-w-[520px] max-h-[90dvh] overflow-y-auto shadow-[0_24px_80px_rgba(0,0,0,0.15)] animate-[scaleIn_0.25s_ease-out]">
        <button
          onClick={onClose}
          className="absolute top-4 right-4 w-8 h-8 rounded-full bg-warm-bg flex items-center justify-center text-text-muted hover:text-charcoal transition-colors border-none cursor-pointer text-lg"
        >
          &times;
        </button>
        <h3 className="font-serif text-xl mb-1">
          Message {profile.first_name}
        </h3>
        <p className="text-[13px] text-text-muted mb-5">
          From {employer.company_name}
        </p>
        <EmployerContactRequirements contact={contact} />
        <textarea
          ref={textareaRef}
          value={message}
          onChange={(e) => setMessage(e.target.value)}
          placeholder={`Hi ${profile.first_name}, I came across your profile on PeakWave and thought you'd be a great fit for...`}
          rows={5}
          className="w-full p-4 border-[1.5px] border-border rounded-md text-sm font-sans bg-warm-bg outline-none focus:border-gold resize-none"
        />
        {error && (
          <p className="text-xs text-error mt-2">{error}</p>
        )}
        <div className="flex justify-end gap-3 mt-4">
          <button
            onClick={onClose}
            className="px-5 py-2.5 text-[13px] font-semibold text-text-muted bg-transparent border-[1.5px] border-border rounded-sm cursor-pointer font-sans hover:border-charcoal"
          >
            Cancel
          </button>
          <button
            onClick={handleSend}
            disabled={!message.trim() || sending}
            className="px-5 py-2.5 text-[13px] font-semibold text-white bg-gold rounded-sm border-none cursor-pointer font-sans transition-all hover:bg-gold-light disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {sending ? "Sending..." : "Send Message"}
          </button>
        </div>
      </div>
    </div>
  );
}

// ═══ VIDEO MODAL ═══

function VideoModal({
  videoUrl,
  onClose,
}: {
  videoUrl: string;
  onClose: () => void;
}) {
  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    document.addEventListener("keydown", handler);
    document.body.style.overflow = "hidden";
    return () => {
      document.removeEventListener("keydown", handler);
      document.body.style.overflow = "";
    };
  }, [onClose]);

  const ytId = getYouTubeId(videoUrl);
  const vimeoId = getVimeoId(videoUrl);

  return (
    <div
      className="fixed inset-0 z-[200] flex items-center justify-center p-4 md:p-8"
      onClick={(e) => e.target === e.currentTarget && onClose()}
    >
      <div className="absolute inset-0 bg-black/80" onClick={onClose} />
      <button
        onClick={onClose}
        className="absolute top-4 right-4 z-20 w-10 h-10 flex items-center justify-center rounded-full bg-white/10 text-white text-xl hover:bg-white/20 transition-colors cursor-pointer border-none"
        aria-label="Close"
      >
        &times;
      </button>
      <div className="relative z-10 w-full max-w-[720px] aspect-video rounded-[12px] overflow-hidden bg-black">
        {ytId ? (
          <iframe
            src={`https://www.youtube-nocookie.com/embed/${ytId}?rel=0&autoplay=1`}
            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
            allowFullScreen
            className="w-full h-full border-none"
          />
        ) : vimeoId ? (
          <iframe
            src={`https://player.vimeo.com/video/${vimeoId}?autoplay=1`}
            allow="autoplay; fullscreen; picture-in-picture"
            allowFullScreen
            className="w-full h-full border-none"
          />
        ) : (
          <video
            controls
            autoPlay
            playsInline
            className="w-full h-full"
          >
            <source src={videoUrl} />
          </video>
        )}
      </div>
    </div>
  );
}

// ═══ WINDOW PICKER MODAL ═══

function WindowPickerModal({
  windows,
  existingConversations,
  candidateName,
  onSelect,
  onClose,
}: {
  windows: AvailabilityWindow[];
  existingConversations: { id: string; status: string; windowId: string | null }[];
  candidateName: string;
  onSelect: (windowId: string) => void;
  onClose: () => void;
}) {
  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    document.addEventListener("keydown", handler);
    document.body.style.overflow = "hidden";
    return () => {
      document.removeEventListener("keydown", handler);
      document.body.style.overflow = "";
    };
  }, [onClose]);

  return (
    <div
      className="fixed inset-0 z-[200] flex items-center justify-center p-4"
      onClick={(e) => e.target === e.currentTarget && onClose()}
    >
      <div className="absolute inset-0 bg-black/50 backdrop-blur-[4px]" />
      <div className="relative z-10 bg-white rounded-[16px] p-5 sm:p-7 w-full max-w-[480px] shadow-[0_24px_80px_rgba(0,0,0,0.15)] animate-[scaleIn_0.25s_ease-out]">
        <button
          onClick={onClose}
          className="absolute top-4 right-4 w-8 h-8 rounded-full bg-warm-bg flex items-center justify-center text-text-muted hover:text-charcoal transition-colors border-none cursor-pointer text-lg"
        >
          &times;
        </button>
        <h3 className="font-serif text-xl mb-1">Which season?</h3>
        <p className="text-[13px] text-text-muted mb-5">
          Choose which season to message {candidateName} about
        </p>
        <div className="flex flex-col gap-2">
          {windows.map((w) => {
            const alreadyMessaged = existingConversations.some(
              (c) => c.windowId === w.id
            );
            const roleLabels = w.positions
              .map((p) => findLabelById(ROLES_TREE, p))
              .filter(Boolean)
              .slice(0, 3);

            return (
              <button
                key={w.id}
                onClick={() => !alreadyMessaged && onSelect(w.id)}
                disabled={alreadyMessaged}
                className={`w-full px-4 py-3 rounded-[10px] text-left font-sans border-[1.5px] transition-all ${
                  alreadyMessaged
                    ? "bg-warm-bg/50 border-border-light text-text-muted cursor-not-allowed opacity-60"
                    : "bg-warm-bg border-border-light cursor-pointer hover:border-gold hover:bg-gold/[0.03]"
                }`}
              >
                <div className="flex items-center justify-between gap-2 mb-1">
                  <span className="text-[14px] font-semibold text-charcoal">
                    {w.season_label}
                  </span>
                  {alreadyMessaged && (
                    <span className="text-[10px] font-semibold px-2 py-0.5 rounded-full bg-navy/8 text-text-muted shrink-0">
                      Already messaged
                    </span>
                  )}
                </div>
                <p className="text-[12px] text-text-muted mb-1.5">
                  {new Date(w.available_from).toLocaleDateString("en-GB", {
                    month: "short",
                    year: "numeric",
                  })}{" "}
                  &ndash;{" "}
                  {new Date(w.available_to).toLocaleDateString("en-GB", {
                    month: "short",
                    year: "numeric",
                  })}
                </p>
                {roleLabels.length > 0 && (
                  <div className="flex gap-1 flex-wrap">
                    {roleLabels.map((r) => (
                      <span
                        key={r}
                        className="bg-gold/[0.08] text-gold px-2 py-0.5 rounded-[5px] text-[10px] font-medium"
                      >
                        {r}
                      </span>
                    ))}
                  </div>
                )}
              </button>
            );
          })}
        </div>
      </div>
    </div>
  );
}

// ═══ MAIN COMPONENT ═══

export function CandidateProfileContent({
  profile,
  availabilityWindows = [],
  placementMap = {},
  userRole,
  employer,
  existingConversations = [],
  isShortlisted: initialShortlisted,
  isSelf = false,
  referralCount = 0,
  referralCode = null,
  linkedProfiles = [],
  vouches: initialVouches = [],
  currentUserHasVouched: initialHasVouched = false,
}: {
  profile: Profile;
  availabilityWindows?: AvailabilityWindow[];
  placementMap?: Record<string, PlacementDateRange[]>;
  userRole?: string;
  employer?: EmployerInfo | null;
  existingConversations?: { id: string; status: string; windowId: string | null }[];
  isShortlisted: boolean;
  isSelf?: boolean;
  referralCount?: number;
  referralCode?: string | null;
  linkedProfiles?: LinkedProfile[];
  vouches?: Vouch[];
  currentUserHasVouched?: boolean;
}) {
  const router = useRouter();
  const [isShortlisted, setIsShortlisted] = useState(initialShortlisted);
  const [showMessageModal, setShowMessageModal] = useState(false);
  const [windowPickerOpen, setWindowPickerOpen] = useState(false);
  const [selectedWindowId, setSelectedWindowId] = useState<string | null>(null);
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const [showVideoModal, setShowVideoModal] = useState(false);
  const [expandedLocations, setExpandedLocations] = useState<Set<string>>(new Set());
  const [vouches, setVouches] = useState<Vouch[]>(initialVouches);
  const [hasVouched, setHasVouched] = useState(initialHasVouched);
  const [showVouchModal, setShowVouchModal] = useState(false);

  const isEmployer = userRole === "employer";
  const activity = getActivityInfo(profile.last_active);
  const gradient =
    AVATAR_GRADIENTS[
      profile.first_name.charCodeAt(0) % AVATAR_GRADIENTS.length
    ];

  const qualLabels = profileQualificationRecords(profile).map((record) => {
    const label = findLabelById(QUALIFICATIONS_TREE, record.id) || record.id;
    if (record.status === "training") return `${label} · In training${record.expected_on ? `, expected ${record.expected_on}` : ""}`;
    if (record.expires_on) return `${label} · ${record.expires_on < new Date().toISOString().slice(0, 10) ? "Expired" : "Expires"} ${record.expires_on}`;
    return label;
  });
  const languages = parseLanguages(profile.languages);

  // Sort windows: active first (by date asc), placed last (by date desc)
  const sortedWindows = [...availabilityWindows].sort((a, b) => {
    if (a.status === "placed" && b.status !== "placed") return 1;
    if (a.status !== "placed" && b.status === "placed") return -1;
    if (a.status === "placed" && b.status === "placed") {
      return new Date(b.available_from).getTime() - new Date(a.available_from).getTime();
    }
    return new Date(a.available_from).getTime() - new Date(b.available_from).getTime();
  });
  const activeWindows = sortedWindows.filter((w) => w.status !== "placed");
  const placedWindows = sortedWindows.filter((w) => w.status === "placed");

  // Windows that can receive a new message from this employer
  const messageableWindows = activeWindows.filter(
    (w) => !existingConversations.some((c) => c.windowId === w.id)
  );

  const isCandidate = userRole === "candidate";
  const isLoggedOut = !userRole;

  useEffect(() => {
    if (!isEmployer || isSelf) return;
    trackEvent("candidate_profile_viewed", {
      candidate_profile_id: profile.id,
      availability_window_count: activeWindows.length,
      has_video: Boolean(profile.video_url),
      is_sample: profile.is_sample,
    });
  }, [
    activeWindows.length,
    isEmployer,
    isSelf,
    profile.id,
    profile.is_sample,
    profile.video_url,
  ]);

  function handleMessageClick() {
    if (profile.is_sample) return;
    if (isCandidate) return;
    if (isLoggedOut) {
      router.push(`/employer-signup?redirect=/candidates/${profile.id}`);
      return;
    }

    // No messageable windows — redirect to existing conversation
    if (messageableWindows.length === 0) {
      const firstConv = existingConversations[0];
      if (firstConv) {
        router.push(`/employer/messages/${firstConv.id}`);
      }
      return;
    }

    // Only 1 messageable window — auto-select and open compose
    if (messageableWindows.length === 1) {
      setSelectedWindowId(messageableWindows[0].id);
      setShowMessageModal(true);
      return;
    }

    // Multiple windows — show picker
    setWindowPickerOpen(true);
  }

  function handleWindowSelected(windowId: string) {
    setSelectedWindowId(windowId);
    setWindowPickerOpen(false);
    setShowMessageModal(true);
  }

  async function handleToggleShortlist() {
    if (isCandidate) return;
    if (!isEmployer) {
      router.push(`/employer-signup?redirect=/candidates/${profile.id}`);
      return;
    }
    setIsShortlisted((prev) => !prev);
    toggleShortlist(profile.id).then((result) => {
      if (result.error) {
        setIsShortlisted((prev) => !prev);
      }
    });
    trackEvent("shortlist_toggled", { candidateId: profile.id });
  }

  return (
    <div className="max-w-[860px] mx-auto px-5 md:px-8 pt-6 md:pt-10 pb-16 md:pb-20">
      {/* Self-view banner */}
      {isSelf && (
        <>
          <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3 bg-gold-pale border border-gold/20 rounded-[14px] px-5 py-4 mb-4">
            <p className="text-[14px] text-text-body">
              This is how signed-in employers see your profile. Anyone else
              who opens this link, including search engines, only sees your
              first name, roles and seasons.
            </p>
            <div className="flex gap-2 shrink-0">
              <Link
                href="/profile?setup=work#availability"
                className="px-4 py-2 rounded-sm text-[13px] font-semibold text-gold border-[1.5px] border-gold/30 no-underline hover:bg-gold/5 transition-all whitespace-nowrap"
              >
                Edit Availability
              </Link>
              <Link
                href="/profile"
                className="px-4 py-2 rounded-sm text-[13px] font-semibold bg-gold text-white no-underline hover:bg-gold-light transition-all whitespace-nowrap"
              >
                Edit Profile
              </Link>
            </div>
          </div>
          {/* Referral progress (self-view) */}
          {!profile.featured && referralCode && (
            <div className="flex items-center gap-3 bg-warm-bg border border-border-light rounded-[14px] px-5 py-3.5 mb-4">
              <span className="text-gold text-lg">&#9733;</span>
              <p className="text-[13px] text-text-body">
                <span className="font-semibold">{referralCount}/3 referrals</span>
                {referralCount < 3
                  ? `, refer ${3 - referralCount} more ${3 - referralCount === 1 ? "friend" : "friends"} to get Featured status`
                  : ""}
              </p>
            </div>
          )}
          <ProfileShareCard profileId={profile.id} />
        </>
      )}

      {/* Back link */}
      <Link
        href={isSelf ? "/profile" : "/browse"}
        className="inline-flex items-center gap-1.5 text-[13px] font-medium text-text-muted no-underline hover:text-navy transition-colors mb-6"
      >
        <svg
          width="16"
          height="16"
          viewBox="0 0 16 16"
          fill="none"
          stroke="currentColor"
          strokeWidth="1.5"
          strokeLinecap="round"
          strokeLinejoin="round"
        >
          <path d="M10 12L6 8L10 4" />
        </svg>
        {isSelf ? "Back to Profile" : "Browse Candidates"}
      </Link>

      {/* Sample profile note */}
      {profile.is_sample && (
        <div className="text-[12px] text-text-muted bg-warm-bg border border-dashed border-border-light rounded-sm px-4 py-2.5 mb-5">
          This is a sample profile to show how PeakWave works.
        </div>
      )}

      {/* Header card */}
      <div className="bg-white rounded-[16px] p-6 md:p-8 border border-border-light mb-5">
        <div className="flex flex-col md:flex-row gap-5 md:gap-7">
          {/* Photo */}
          <div className="relative shrink-0">
            <div
              className={`w-24 h-24 rounded-[18px] bg-gradient-to-br ${gradient} flex items-center justify-center font-serif text-3xl text-white font-semibold overflow-hidden ${profile.photo_url ? "cursor-pointer" : ""} ${profile.featured ? "ring-[2.5px] ring-gold ring-offset-2" : ""}`}
              onClick={() => {
                if (profile.photo_url) setLightboxOpen(true);
              }}
            >
              {profile.photo_url ? (
                // eslint-disable-next-line @next/next/no-img-element
                <img
                  src={profile.photo_url}
                  alt={`${profile.first_name} ${profile.last_name}`}
                  className="w-full h-full object-cover"
                />
              ) : (
                getInitials(profile.first_name, profile.last_name)
              )}
            </div>
            {profile.featured && (
              <span className="absolute -bottom-1 -right-1 w-6 h-6 bg-gold rounded-full flex items-center justify-center border-2 border-white text-white text-[11px]">&#9733;</span>
            )}
          </div>
          {lightboxOpen && profile.photo_url && (
            <PhotoLightbox
              src={profile.photo_url}
              alt={`${profile.first_name} ${profile.last_name}`}
              onClose={() => setLightboxOpen(false)}
            />
          )}

          {/* Info */}
          <div className="flex-1 min-w-0">
            <h1 className="font-serif text-[28px] md:text-[32px] font-normal mb-1">
              {profile.first_name} {profile.last_name}
              {(() => {
                const dateStr = profile.birth_date ?? profile.dob;
                const age = dateStr ? getAge(dateStr) : null;
                return age ? (
                  <span className="font-sans font-normal text-text-muted">, {age}</span>
                ) : null;
              })()}
              {profile.featured && profile.boost_source === 'season_edit' ? (
                <span className="relative inline-flex items-center gap-1 ml-2 align-middle bg-gold/10 text-gold px-2.5 py-0.5 rounded-full text-[12px] font-sans font-semibold group cursor-default">
                  <span className="text-[11px]">&#9733;</span> Featured on the Season Edit
                  <span className="invisible group-hover:visible group-focus:visible absolute left-1/2 -translate-x-1/2 top-full mt-2 w-[240px] px-3 py-2 rounded-lg bg-navy text-white text-[11px] font-normal leading-snug text-center shadow-lg z-50 pointer-events-none" role="tooltip">
                    Photo or story featured on The Edit. Contributors get a 3-week profile boost.
                  </span>
                </span>
              ) : profile.featured ? (
                <span className="relative inline-flex items-center gap-1 ml-2 align-middle bg-gold/10 text-gold px-2.5 py-0.5 rounded-full text-[12px] font-sans font-semibold group cursor-default">
                  <span className="text-[11px]">&#9733;</span> Featured
                  <span className="invisible group-hover:visible group-focus:visible absolute left-1/2 -translate-x-1/2 top-full mt-2 w-[220px] px-3 py-2 rounded-lg bg-navy text-white text-[11px] font-normal leading-snug text-center shadow-lg z-50 pointer-events-none" role="tooltip">
                    Featured candidates are active community members who&apos;ve been recognised by PeakWave.
                  </span>
                </span>
              ) : null}
            </h1>

            {/* Location, nationality & activity */}
            <div className="text-[13px] text-text-muted mb-1">
              {profile.current_location && (
                <>
                  <svg
                    className="inline-block w-3.5 h-3.5 mr-0.5 -mt-0.5"
                    viewBox="0 0 24 24"
                    fill="currentColor"
                  >
                    <path d="M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7zm0 9.5a2.5 2.5 0 0 1 0-5 2.5 2.5 0 0 1 0 5z" />
                  </svg>
                  <span>Based in {profile.current_location}</span>
                  <span> &middot; </span>
                </>
              )}
              {profile.nationality && (
                <><span>{getInformalNationality(profile.nationality)}</span><span> &middot; </span></>
              )}
              <span className="inline-flex items-center gap-1">
                {activity.dotClass && <span className={`w-1.5 h-1.5 rounded-full ${activity.dotClass} inline-block`} />}
                {activity.label}
              </span>
            </div>

            {/* Experience level */}
            {profile.experience_level && (
              <div className="mb-1">
                <span className="inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-[11px] font-semibold bg-gold-pale text-gold">
                  {EXPERIENCE_LEVELS.find((e) => e.value === profile.experience_level)?.label ?? profile.experience_level}{" "}
                  experience
                </span>
              </div>
            )}

            {profile.is_sample && (
              <span className="inline-block px-2.5 py-0.5 rounded-full text-[11px] font-semibold bg-border/40 text-text-muted mt-1">
                Sample Profile
              </span>
            )}

            {/* Watch Video button */}
            {profile.video_url && (
              <button
                onClick={() => setShowVideoModal(true)}
                className="mt-3 inline-flex items-center gap-2 bg-transparent border-none cursor-pointer p-0 group"
                aria-label="Play video intro"
              >
                <span className="w-8 h-8 rounded-full bg-navy flex items-center justify-center shrink-0 transition-transform group-hover:scale-[1.08]">
                  <svg width="12" height="12" viewBox="0 0 10 10" fill="white">
                    <polygon points="3.5,1.5 8,5 3.5,8.5" />
                  </svg>
                </span>
                <span className="text-[13px] text-text-muted font-sans font-medium group-hover:text-navy transition-colors">Video Intro</span>
              </button>
            )}

            {/* Bio */}
            {profile.bio && (
              <p className="text-[14px] text-text-body leading-[1.7] whitespace-pre-line mt-3">
                {profile.bio}
              </p>
            )}
          </div>

          {/* Action buttons */}
          {(() => {
            // Completely disabled: self-view or sample profile
            const hardDisabled = isSelf || profile.is_sample;
            // Candidate viewing another candidate: greyed out with tooltip
            const candidateViewing = isCandidate && !isSelf && !profile.is_sample;
            // Logged out: redirect to employer signup
            const loggedOutViewing = isLoggedOut && !profile.is_sample;

            const tooltipText = isSelf
              ? "Employers can message and shortlist you directly from your profile."
              : profile.is_sample
                ? "This is a sample profile."
                : candidateViewing
                  ? "Sign up as an employer to message candidates"
                  : null;

            return (
              <div className="flex flex-row md:flex-col gap-2 shrink-0">
                {/* Message button */}
                <div className="relative group">
                  {hardDisabled || candidateViewing ? (
                    <button
                      disabled
                      className="px-5 py-2.5 rounded-sm text-[13px] font-semibold border-none font-sans whitespace-nowrap bg-border/50 text-text-muted cursor-not-allowed"
                      title={tooltipText ?? undefined}
                    >
                      Message
                    </button>
                  ) : loggedOutViewing ? (
                    <button
                      onClick={() => router.push(`/employer-signup?redirect=/candidates/${profile.id}`)}
                      className="px-5 py-2.5 rounded-sm text-[13px] font-semibold border-none font-sans whitespace-nowrap bg-gold text-white cursor-pointer transition-all hover:bg-gold-light hover:-translate-y-0.5"
                    >
                      Sign up to message
                    </button>
                  ) : (
                    <button
                      onClick={handleMessageClick}
                      className="px-5 py-2.5 rounded-sm text-[13px] font-semibold border-none font-sans whitespace-nowrap bg-gold text-white cursor-pointer transition-all hover:bg-gold-light hover:-translate-y-0.5"
                    >
                      {messageableWindows.length === 0 && existingConversations.length > 0
                        ? "View Conversation"
                        : "Message"}
                    </button>
                  )}
                  {tooltipText && !candidateViewing && (
                    <span className="pointer-events-none absolute bottom-full left-1/2 -translate-x-1/2 mb-2 hidden group-hover:block w-48 sm:w-56 text-center text-[11px] text-white bg-charcoal rounded-[8px] px-3 py-2 shadow-lg z-10">
                      {tooltipText}
                    </span>
                  )}
                </div>

                {/* Save button */}
                <div className="relative group">
                  <button
                    onClick={(hardDisabled || candidateViewing) ? undefined : (loggedOutViewing ? () => router.push(`/employer-signup?redirect=/candidates/${profile.id}`) : handleToggleShortlist)}
                    disabled={hardDisabled || candidateViewing}
                    aria-label={isShortlisted ? "Remove from shortlist" : "Save to shortlist"}
                    title={candidateViewing ? "Sign up as an employer to message candidates" : undefined}
                    className={`px-5 py-2.5 rounded-sm text-[13px] font-semibold border-[1.5px] font-sans whitespace-nowrap inline-flex items-center justify-center gap-1.5 ${
                      hardDisabled || candidateViewing
                        ? "bg-transparent text-text-muted/50 border-border/50 cursor-not-allowed"
                        : isShortlisted
                          ? "bg-gold/8 text-gold border-gold/30 cursor-pointer transition-all hover:bg-transparent hover:border-border hover:text-text-muted"
                          : "bg-transparent text-text-muted border-border cursor-pointer transition-all hover:border-gold hover:text-gold"
                    }`}
                  >
                    <svg
                      width="14"
                      height="14"
                      viewBox="0 0 24 24"
                      fill={!(hardDisabled || candidateViewing) && isShortlisted ? "currentColor" : "none"}
                      stroke="currentColor"
                      strokeWidth="2.5"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    >
                      <polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2" />
                    </svg>
                    {!(hardDisabled || candidateViewing) && isShortlisted ? "Saved" : "Save"}
                  </button>
                </div>
              </div>
            );
          })()}
        </div>
      </div>

      {/* Availability Windows */}
      {sortedWindows.length > 0 && (
        <div className="bg-white rounded-[16px] p-6 md:p-8 border border-border-light mb-5">
          <label className="text-[10px] font-semibold text-gold uppercase tracking-[1px] block mb-3">
            Availability
          </label>
          <div className="flex flex-col gap-3">
            {activeWindows.map((w) => (
              <div key={w.id} className="p-4 rounded-[12px] bg-warm-bg border border-border-light">
                <div className="flex items-center gap-2 flex-wrap mb-2">
                  <span className="text-[14px] font-semibold text-charcoal">{w.season_label}</span>
                  <span className={`px-2 py-0.5 rounded-full text-[10px] font-semibold ${
                    w.status === "actively_looking" ? "bg-[#E8F5E9] text-success" : "bg-[#FFF8E1] text-[#F57F17]"
                  }`}>
                    {w.status === "actively_looking" ? "Actively Looking" : "Open to Opportunities"}
                  </span>
                </div>
                <p className="text-[13px] text-text-muted mb-2">
                  {new Date(w.available_from).toLocaleDateString("en-GB", { month: "short", year: "numeric" })} &ndash;{" "}
                  {new Date(w.available_to).toLocaleDateString("en-GB", { month: "short", year: "numeric" })}
                  {" "}&middot; {w.dates_flexible ? "Flexible" : "Fixed"}
                  {w.dates_flexible && w.flexible_earliest_start && (
                    <span> &middot; Earliest start: {new Date(w.flexible_earliest_start).toLocaleDateString("en-GB", { month: "short", day: "numeric" })}</span>
                  )}
                  {w.dates_flexible && w.flexible_latest_end && (
                    <span> &middot; Latest end: {new Date(w.flexible_latest_end).toLocaleDateString("en-GB", { month: "short", day: "numeric" })}</span>
                  )}
                </p>
                {w.positions.length > 0 && (
                  <div className="flex gap-[5px] flex-wrap mb-1.5">
                    {w.positions.map((p) => (
                      <span key={p} className="bg-gold/[0.08] text-gold px-[11px] py-1 rounded-[6px] text-[11px] font-medium">
                        {findLabelById(ROLES_TREE, p) ?? p}
                      </span>
                    ))}
                  </div>
                )}
                {w.preferred_locations.length > 0 && (
                  <div className="flex gap-[5px] flex-wrap items-center">
                    {(expandedLocations.has(w.id) ? w.preferred_locations : w.preferred_locations.slice(0, 5)).map((l) => (
                      <span key={l} className="px-[11px] py-1 rounded-[6px] text-[11px] font-medium bg-white text-charcoal border border-border-light">
                        {findLabelById(LOCATIONS_TREE, l) ?? l}
                      </span>
                    ))}
                    {w.preferred_locations.length > 5 && (
                      <button
                        type="button"
                        onClick={() => {
                          setExpandedLocations((prev) => {
                            const next = new Set(prev);
                            if (next.has(w.id)) next.delete(w.id);
                            else next.add(w.id);
                            return next;
                          });
                        }}
                        className="text-[11px] text-text-muted hover:text-text-body transition-colors cursor-pointer border-none bg-transparent p-0"
                      >
                        {expandedLocations.has(w.id) ? "Show less" : `+ ${w.preferred_locations.length - 5} more`}
                      </button>
                    )}
                  </div>
                )}
                {/* Placement info for this window */}
                {(placementMap[w.id] ?? []).length > 0 && (
                  <div className="mt-2 pt-2 border-t border-border-light/60">
                    {(placementMap[w.id] ?? []).map((p, i) => (
                      <p key={i} className="text-[12px] text-[#5A6B82] font-medium">
                        Placed: {new Date(p.start).toLocaleDateString("en-GB", { day: "numeric", month: "short" })} to {new Date(p.end).toLocaleDateString("en-GB", { day: "numeric", month: "short" })}
                      </p>
                    ))}
                    <p className="text-[12px] text-success font-medium mt-0.5">
                      Available for other dates
                    </p>
                  </div>
                )}
              </div>
            ))}
            {placedWindows.map((w) => {
              const windowPlacements = placementMap[w.id] ?? [];
              return (
              <div key={w.id} className="p-4 rounded-[12px] bg-warm-bg/50 border border-border-light opacity-60">
                <div className="flex items-center gap-2 flex-wrap mb-1">
                  <span className="text-[14px] font-semibold text-charcoal">{w.season_label}</span>
                  <span className="px-2 py-0.5 rounded-full text-[10px] font-semibold bg-[#E3F2FD] text-[#1565C0]">
                    Fully placed
                  </span>
                </div>
                <p className="text-[13px] text-text-muted">
                  {new Date(w.available_from).toLocaleDateString("en-GB", { month: "short", year: "numeric" })} &ndash;{" "}
                  {new Date(w.available_to).toLocaleDateString("en-GB", { month: "short", year: "numeric" })}
                </p>
                {windowPlacements.length > 0 && (
                  <div className="mt-1.5">
                    {windowPlacements.map((p, i) => (
                      <p key={i} className="text-[12px] text-text-muted">
                        Placed: {new Date(p.start).toLocaleDateString("en-GB", { day: "numeric", month: "short" })} to {new Date(p.end).toLocaleDateString("en-GB", { day: "numeric", month: "short" })}
                      </p>
                    ))}
                  </div>
                )}
              </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Details grid */}
      {(qualLabels.length > 0 || (profile.right_to_work?.length ?? 0) > 0 || languages.length > 0 || (profile.gender && profile.gender !== "Prefer not to say")) && (
        <div className="bg-white rounded-[16px] p-6 md:p-8 border border-border-light mb-5">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* Qualifications */}
            {qualLabels.length > 0 && (
              <div>
                <label className="text-[10px] font-semibold text-gold uppercase tracking-[1px] block mb-2">
                  Qualifications
                </label>
                <p className="mb-2 text-xs text-text-muted">Self-declared. Certificates have not been verified by PeakWave.</p>
                <div className="flex gap-[5px] flex-wrap">
                  {qualLabels.map((t) => (
                    <span
                      key={t}
                      className="bg-gold/[0.08] text-gold px-[11px] py-1 rounded-[6px] text-[11px] font-medium"
                    >
                      {t}
                    </span>
                  ))}
                </div>
              </div>
            )}

            {/* Right to Work In */}
            {profile.right_to_work?.length > 0 && (
              <div>
                <label className="text-[10px] font-semibold text-gold uppercase tracking-[1px] block mb-2">
                  Right to Work In
                </label>
                <div className="flex gap-1.5 flex-wrap">
                  {profile.right_to_work.map((r: RightToWorkEntry) => (
                    <span
                      key={r.region}
                      className={`px-2.5 py-0.5 rounded-[6px] text-[11px] font-medium border ${
                        r.status === "yes"
                          ? "bg-[#E8F5E9] text-success border-[#C8E6C9]"
                          : "bg-[#FFF8E1] text-[#F57F17] border-[#FFE082]"
                      }`}
                    >
                      {r.region} {r.status === "yes" ? "\u2713" : "(sponsorship)"}
                    </span>
                  ))}
                </div>
              </div>
            )}

            {/* Languages */}
            {languages.length > 0 && (
              <div>
                <label className="text-[10px] font-semibold text-gold uppercase tracking-[1px] block mb-2">
                  Languages
                </label>
                <div className="flex gap-[5px] flex-wrap">
                  {languages.map((l) => (
                    <span
                      key={l.language}
                      className="px-[11px] py-1 rounded-[6px] text-[11px] font-medium bg-warm-bg text-charcoal border border-border-light"
                    >
                      {l.language}
                      <span className="text-text-muted ml-1">
                        ({l.proficiency})
                      </span>
                    </span>
                  ))}
                </div>
              </div>
            )}

            {/* Gender */}
            {profile.gender && profile.gender !== "Prefer not to say" && (
              <div>
                <label className="text-[10px] font-semibold text-gold uppercase tracking-[1px] block mb-2">
                  Gender
                </label>
                <span className="text-[14px] text-text-body">{profile.gender}</span>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Travelling with */}
      {linkedProfiles.length > 0 && (
        <div className="bg-white rounded-[16px] p-6 md:p-8 border border-border-light mb-5">
          <label className="text-[10px] font-semibold text-gold uppercase tracking-[1px] block mb-4">
            Travelling with
          </label>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            {linkedProfiles.filter((lp) => lp.profile_id).map((lp) => (
              <Link
                key={lp.user_id ?? lp.profile_id}
                href={`/candidates/${lp.profile_id}`}
                className="flex items-center gap-3 bg-warm-bg rounded-[12px] px-4 py-3 hover:border-gold/30 border border-transparent transition-all no-underline group"
              >
                <div className="w-10 h-10 rounded-full bg-gradient-to-br from-terracotta to-gold flex items-center justify-center text-white text-sm font-semibold shrink-0 overflow-hidden">
                  {lp.photo_url ? (
                    <Image
                      src={lp.photo_url}
                      alt={`${lp.first_name} ${lp.last_name}`}
                      width={40}
                      height={40}
                      sizes="40px"
                      className="w-full h-full object-cover"
                    />
                  ) : (
                    `${lp.first_name.charAt(0)}${lp.last_name.charAt(0)}`
                  )}
                </div>
                <div className="min-w-0">
                  <p className="text-[14px] font-medium text-charcoal group-hover:underline decoration-charcoal/30 underline-offset-2 truncate">
                    {lp.first_name} {lp.last_name}
                  </p>
                  {lp.positions.length > 0 && (
                    <div className="flex gap-1 flex-wrap mt-1">
                      {lp.positions.slice(0, 2).map((p) => (
                        <span
                          key={p}
                          className="bg-gold/[0.08] text-gold px-2 py-0.5 rounded-[5px] text-[10px] font-medium"
                        >
                          {findLabelById(ROLES_TREE, p) ?? p}
                        </span>
                      ))}
                    </div>
                  )}
                </div>
                <span className="ml-auto px-2 py-0.5 rounded-full text-[10px] font-medium bg-gold/[0.08] text-gold shrink-0">
                  {lp.relationship_type === "couple" ? "Couple" : lp.relationship_type === "friends" ? "Friends" : "Group"}
                </span>
              </Link>
            ))}
          </div>
        </div>
      )}

      {/* Vouches */}
      {(vouches.length > 0 || (isCandidate && !isSelf)) && (
        <div className="bg-white rounded-[16px] p-6 md:p-8 border border-border-light mb-5">
          <div className="flex items-center justify-between mb-4">
            <label className="text-[10px] font-semibold text-gold uppercase tracking-[1px]">
              Vouches{vouches.length > 0 ? ` (${vouches.length})` : ""}
            </label>
            {isCandidate && !isSelf && !hasVouched && (
              <button
                onClick={() => setShowVouchModal(true)}
                className="px-3.5 py-1.5 text-[12px] font-semibold text-gold bg-gold/8 border border-gold/20 rounded-sm cursor-pointer font-sans transition-all hover:bg-gold/15"
              >
                Vouch for {profile.first_name}
              </button>
            )}
            {isCandidate && !isSelf && hasVouched && (
              <span className="px-3.5 py-1.5 text-[12px] font-semibold text-success bg-[#E8F5E9] rounded-sm">
                You&apos;ve vouched for {profile.first_name}
              </span>
            )}
          </div>
          {vouches.length > 0 ? (
            <div className="flex flex-col gap-3">
              {vouches.map((v) => (
                <div
                  key={v.id}
                  className="flex gap-3 p-3.5 rounded-[12px] bg-warm-bg border border-border-light"
                >
                  <Link
                    href={v.voucher_profile_id ? `/candidates/${v.voucher_profile_id}` : "#"}
                    className="shrink-0 no-underline"
                  >
                    <div className="w-9 h-9 rounded-full bg-gradient-to-br from-sage to-[#5A7A5F] flex items-center justify-center text-white text-sm font-semibold overflow-hidden">
                      {v.voucher_photo_url ? (
                        <Image
                          src={v.voucher_photo_url}
                          alt={`${v.voucher_first_name} ${v.voucher_last_name}`}
                          width={36}
                          height={36}
                          sizes="36px"
                          className="w-full h-full object-cover"
                        />
                      ) : (
                        `${v.voucher_first_name.charAt(0)}${v.voucher_last_name.charAt(0) || ""}`.toUpperCase()
                      )}
                    </div>
                  </Link>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-1.5 flex-wrap">
                      <Link
                        href={v.voucher_profile_id ? `/candidates/${v.voucher_profile_id}` : "#"}
                        className="text-[13px] font-semibold text-charcoal no-underline hover:underline decoration-charcoal/30 underline-offset-2"
                      >
                        {v.voucher_first_name} {v.voucher_last_name}
                      </Link>
                      <span className="text-[11px] text-text-muted">{v.relationship}</span>
                    </div>
                    {v.message && (
                      <p className="text-[13px] text-text-body leading-relaxed mt-1">
                        &ldquo;{v.message}&rdquo;
                      </p>
                    )}
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <p className="text-[13px] text-text-muted">
              No vouches yet. Know {profile.first_name}? Be the first to vouch for them.
            </p>
          )}
        </div>
      )}

      {/* Vouch modal */}
      {showVouchModal && (
        <VouchModal
          candidateName={profile.first_name}
          voucheeId={profile.user_id}
          onClose={() => setShowVouchModal(false)}
          onVouched={(vouch) => {
            setVouches((prev) => [vouch, ...prev]);
            setHasVouched(true);
            setShowVouchModal(false);
          }}
        />
      )}

      {/* Video modal */}
      {showVideoModal && profile.video_url && (
        <VideoModal
          videoUrl={profile.video_url}
          onClose={() => setShowVideoModal(false)}
        />
      )}

      {/* Message modal */}
      {showMessageModal && employer && selectedWindowId && (
        <MessageModal
          profile={profile}
          employer={employer}
          availabilityWindowId={selectedWindowId}
          onClose={() => {
            setShowMessageModal(false);
            setSelectedWindowId(null);
          }}
          onSent={(conversationId) => {
            setShowMessageModal(false);
            setSelectedWindowId(null);
            router.push(`/employer/messages/${conversationId}`);
          }}
        />
      )}

      {/* Window Picker Modal */}
      {windowPickerOpen && (
        <WindowPickerModal
          windows={activeWindows}
          existingConversations={existingConversations}
          candidateName={profile.first_name}
          onSelect={handleWindowSelected}
          onClose={() => setWindowPickerOpen(false)}
        />
      )}
    </div>
  );
}
